import { AfterViewInit, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { PortalModuleBlueprint } from '@src/app/model/portal-module-blueprint';
import { PortalModuleVariant } from '@src/app/model/portal-module-variant';
import { Observable, Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { AssetService } from '@src/app/services/asset/asset.service';
import {
  archivedStatus,
  Asset,
  draftStatus,
  Events,
  Id,
  id,
  ModuleBlueprint,
  publishedStatus,
} from '@ess/jg-rule-executor';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConfirmationModalComponent } from '@src/app/components/modals/confirmation-modal/confirmation-modal.component';
import _ from 'lodash';
import { RuleEditorModalComponent } from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.component';
import { cloneDeep } from 'lodash';
import { PortalInvariant } from '@src/app/model/portal-invariant';
import { AssetDialogComponent } from '@src/app/components/asset-dialog/asset-dialog.component';
import { NewBlueprintComponent } from '@src/app/components/blueprints/new-blueprint/new-blueprint.component';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  ModuleBlueprintInput,
  ModuleVariantInput,
  SubjectType,
  VersionedStatusTypes,
} from '@src/app/generated/globalTypes';
import {
  createRuleListFromEvents,
  createRuleListFromInvariants,
  RuleData,
} from '@src/app/components/modals/rule-editor-modal/rule-editor-modal.utils';
import { JsonModal } from '@src/app/components/utils/json-modal';
import { TypedFormModal } from '@src/app/components/utils/typed-form-modal';
import { TypedFormModalDataObject } from '@src/app/model/typed-form-modal-data-object';
import { fixedEnvironment } from '@src/environments/environment.fixed';

@Component({
  selector: 'app-blueprint-list',
  templateUrl: './blueprint-list.component.html',
  styleUrls: ['./blueprint-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
})
export class BlueprintListComponent implements OnInit, AfterViewInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  public displayedColumnsBlueprints: string[] = ['id', 'name', 'action'];
  public displayedColumnsVariants: string[] = ['id', 'name', 'sku', 'action'];
  public currentBlueprintItems: PortalModuleBlueprint[] = [];
  public filteredBlueprintItems: MatTableDataSource<PortalModuleBlueprint>
    = new MatTableDataSource<PortalModuleBlueprint>([]);

  public statusOptions: string[] = [draftStatus.__typename, publishedStatus.__typename, archivedStatus(0).__typename];
  public statusFilter: string = this.statusOptions[0];
  public currentStatus: string = draftStatus.__typename;
  public expandedVariants: PortalModuleVariant[] = [];
  public expandedBpId: Id<ModuleBlueprint>;

  private _blueprintFailMsg: string = 'blueprints.upload.failure';
  private _eventFailMsg: string = 'blueprints.upload.failure';
  private _variantFailMsg: string = 'blueprints.uploadVariants.failure';
  private _assetFailMsg: string = 'blueprints.uploadAsset.failure';
  private _assetSuccessMsg: string = 'blueprints.uploadAsset.success';

  private _initialLoad: boolean = true;
  readonly _blueprintSkipFields: string[] = ['id', 'variants', 'events', 'invariants', 'model', 'groupId', 'status', 'categoryId', 'history'];
  readonly _variantSkipFields: string[] = ['id', 'groupId', 'blueprintId', 'featureImage', 'model', 'catalogIcon'];

  readonly sort = viewChild(MatSort);

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _assetService: AssetService,
    private _translateService: TranslateService
  ) {
  }

  public ngOnInit(): void {
    this.getCurrentBlueprints();
    this._apolloService.statusFilter = this.statusFilter;
  }

  public ngAfterViewInit(): void {
    this.filteredBlueprintItems.sort = this.sort();
    this.filteredBlueprintItems.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'status.version':
          if (item.status.__typename === 'Archived') {
            return item.status.version;
          }
          break;
        default:
          return item[property];
      }
    };
  }

  public getCurrentBlueprints(): void {
    this._apolloService.getCurrentBlueprints$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((blueprintsAndPageData) => {
        if (blueprintsAndPageData.blueprints && this.statusFilter !== archivedStatus(0).__typename) {
          this.currentBlueprintItems = blueprintsAndPageData.blueprints;
          this.filteredBlueprintItems.data = this._filterBlueprintItems(this.currentBlueprintItems, this.statusFilter);
          this.expandedVariants = [];
          this.displayedColumnsBlueprints = ['id', 'name', 'action'];
        }
      });
  }

  public getArchivedBlueprints(): void {
    if (this._initialLoad) {
      this._initialLoad = false;
      this._apolloService.getArchivedBlueprints$()
        .pipe(takeUntil(this._componentDestroyed$))
        .subscribe((blueprintsAndPageData) => {
          if (blueprintsAndPageData.blueprints && this.statusFilter === archivedStatus(0).__typename) {
            this.currentBlueprintItems = blueprintsAndPageData.blueprints;
            this.filteredBlueprintItems.data = this._filterBlueprintItems(
              this.currentBlueprintItems,
              this.statusFilter
            );
            this.expandedVariants = [];
            this.displayedColumnsBlueprints = ['groupId', 'status.version', 'id', 'name', 'action'];
          }
        });
    } else {
      this._apolloService.getArchivedBlueprintsRefresh();
    }
  }

  public openCopyBlueprintDialog($event: any, blueprint?: PortalModuleBlueprint, name?: string): void {
    // if no existing blueprint is given, use the newBlueprint$ mutation. Else, use copyBlueprint$
    this._openBlueprintJsonDialog($event,
      bp => blueprint ? this._apolloService.copyBlueprint$(blueprint.id, bp) : this._apolloService.newBlueprint$(bp),
      blueprint,
      undefined,
      name
    );
  }

  public openEditBlueprintJsonDialog($event: any, blueprint: PortalModuleBlueprint, readOnly?: boolean): void {
    // use the updateBlueprint mutation
    this._openBlueprintJsonDialog(
      $event,
      bp => this._apolloService.updateBlueprint$(blueprint.id, bp),
      blueprint,
      readOnly
    );
  }

  public openEditBlueprintFormDialog(blueprint: PortalModuleBlueprint): void {
    const dialogRef = TypedFormModal.openTypedFormModalDialog(
      this._dialog, new TypedFormModalDataObject(
        'blueprint',
        SubjectType.ModuleBlueprintType,
        blueprint,
        blueprint.id,
        this._blueprintSkipFields,
      )
    );
    TypedFormModal.useFormDialog(
      dialogRef,
      (
        (edited: ModuleBlueprintInput) =>
          this._apolloService.updateBlueprint$(blueprint.id, PortalModuleBlueprint.toJsonInput(edited))
      ).bind(this),
      this._translateService.instant(this._blueprintFailMsg),
      this._apolloService.getBlueprintsRefresh.bind(this._apolloService)
    );
  }

  public copyBlueprint($event: any, blueprint: PortalModuleBlueprint): void {
    $event.stopPropagation();
    this._apolloService
      .copyBlueprint$(
        blueprint.id,
        BlueprintListComponent._blueprintToInputObject(_.cloneDeep(blueprint)) as JSON
      )
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(() => {
        this.getBlueprintsRefresh();
      });
  }

  getBlueprintsRefresh(): void {
    if (this.currentStatus === VersionedStatusTypes.Archived.toString()) {
      this._apolloService.getArchivedBlueprintsRefresh();
    } else {
      this._apolloService.getCurrentBlueprintsRefresh();
    }
  }

  public publishBlueprint($event: any, blueprint: PortalModuleBlueprint): void {
    $event.stopPropagation();
    const bp = _.cloneDeep(blueprint);
    bp.status = publishedStatus;

    this._openConfirmationModal(
      'blueprints.publish.header',
      'blueprints.publish.content',
      blueprint.name,
      this._apolloService.publishBlueprint$(blueprint.id)
    );
  }

  public deleteBlueprint($event: any, blueprint: PortalModuleBlueprint): void {
    $event.stopPropagation();
    this._openConfirmationModal(
      'blueprints.deletion.header',
      'blueprints.deletion.content',
      blueprint.name,
      this._apolloService.deleteBlueprint$(blueprint.id)
    );
  }

  public archiveBlueprint($event: any, blueprint: PortalModuleBlueprint): void {
    $event.stopPropagation();
    this._openConfirmationModal(
      'blueprints.archive.header',
      'blueprints.archive.content',
      blueprint.name,
      this._apolloService.archiveBlueprint$(blueprint.id)
    );
  }

  public openEditVariantsJsonDialog($event: any, blueprint?: PortalModuleBlueprint, readOnly?: boolean): void {
    $event.stopPropagation();
    this._apolloService.getVariantsByBlueprintId(blueprint.id).subscribe((v) => {
      const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
        'variants',
        (v && v.length) ? v : [new PortalModuleVariant()],
        'blueprints.uploadVariants.header',
        'buttons.submit',
        this._variantSkipFields,
        readOnly || false // TODO: No variants available!
      ));
      JsonModal.useJsonDialog(
        dialogRef,
        (json => this._apolloService.uploadVariants$(blueprint.id, json)).bind(this._apolloService),
        this._translateService.instant(this._variantFailMsg),
        this._apolloService.getBlueprintsRefresh.bind(this._apolloService)
      );
    });
  }

  public openEditVariantsFormDialog($event: any, blueprint?: PortalModuleBlueprint): void {
    $event.stopPropagation();
    this._apolloService.getVariantsByBlueprintId(blueprint.id).subscribe((variants) => {
      const dialogRef = TypedFormModal.openTypedFormModalDialog(
        this._dialog,
        new TypedFormModalDataObject(
          'variants',
          SubjectType.ModuleVariantType,
          variants,
          blueprint.id,
          this._variantSkipFields,
          undefined,
          'variantList.blueprintIdTitle'
        ));
      TypedFormModal.useFormDialog(
        dialogRef,
        ((editedVariants: ModuleVariantInput[]) =>
          this._apolloService.uploadVariants$(
            blueprint.id,
            PortalModuleVariant.toJsonInput(editedVariants)
          )
        ).bind(this._apolloService),
        this._translateService.instant(this._variantFailMsg),
        this._apolloService.getBlueprintsRefresh.bind(this._apolloService)
      );
    });
  }

  public openEditEventsDialog($event: any, blueprint?: PortalModuleBlueprint, type: string = 'invariant', readOnly?: boolean): void {
    $event.stopPropagation();
    const additionalFields = new PortalInvariant(
      id('templateId'), // Will be replaced
      '',
      new Map(),
      new Map(),
      '',
      ''
    );

    // Set up the rule list
    let ruleList: RuleData[] = [];
    if (type === 'invariant') {
      ruleList = createRuleListFromInvariants(blueprint.invariants);
    } else if (type === 'event') {
      ruleList = createRuleListFromEvents(blueprint.events);
    }

    // Open the dialog
    const dialogRef = this._dialog.open(RuleEditorModalComponent, {
      data: {
        ruleTypeText: type,
        ruleList,
        crud: type === 'invariant',
        readOnly: readOnly || false,
        additionalFieldsTemplate: additionalFields,
        baseTranslationKey: 'blueprints.updateEvents',
        showAdditional: type === 'invariant',
      },
      minWidth: fixedEnvironment.jsonModalWidthLarge,
    });

    const updater = (updated: RuleData[]) => {
      // Parse the edited rules
      const updatedBp = cloneDeep(blueprint);
      if (type === 'event') {
        const updatedEvents = updated.map(x => x.ruleCode);
        updatedBp.events = new Events(
          updatedEvents[0] && updatedEvents[0] !== '' ? updatedEvents[0] : undefined,
          updatedEvents[1] && updatedEvents[1] !== '' ? updatedEvents[1] : undefined,
          updatedEvents[2] && updatedEvents[2] !== '' ? updatedEvents[2] : undefined,
          updatedEvents[3] && updatedEvents[3] !== '' ? updatedEvents[3] : undefined
        );
      } else if (type === 'invariant') {
        updatedBp.invariants = updated.map((x) => {
          const updatedInvariant = x.additionalFields as PortalInvariant;
          updatedInvariant.id = id(x.ruleId);
          updatedInvariant.rule = x.ruleCode;
          updatedInvariant.correction = x.correctionCode;
          return updatedInvariant;
        });
      }

      const newBpInput: JSON = PortalModuleBlueprint.toJsonInput(updatedBp.toGqlInput());

      return this._apolloService.updateBlueprint$(updatedBp.id, newBpInput, false);
    };
    const onError = dialogRef.componentInstance.showError.bind(dialogRef.componentInstance);

    JsonModal.useRuleDialog(
      dialogRef,
      updater.bind(this._apolloService),
      this._translateService.instant(this._eventFailMsg, { ruleType: type }),
      this._apolloService.getBlueprintsRefresh.bind(this._apolloService),
      onError
    );
  }

  public assetUpload(asset: PortalModuleBlueprint | PortalModuleVariant, event, type?: string): void {
    event.stopPropagation();
    if (event.target.files.length > 0) {
      const assetMap = BlueprintListComponent.setAssetMap(asset, type);
      this._assetService.uploadAsset$(event.target.files[0], assetMap)
        .pipe(take(1))
        .subscribe({
          next: (a) => {
            if (!a) {
              window.alert(this._translateService.instant(this._assetFailMsg));
            } else {
              this.getBlueprintsRefresh();
              window.alert(this._translateService.instant(this._assetSuccessMsg));
            }
          },
          error: (err) => {
            window.alert(this._translateService.instant(this._assetFailMsg));
          },
        });
    }
  }

  public openAsset(asset: Asset): void {
    this._dialog.open(AssetDialogComponent, { data: { asset } });
  }

  public expandBlueprint(blueprint: PortalModuleBlueprint): void {
    if (blueprint.id === this.expandedBpId) {
      this.expandedVariants = [];
      this.expandedBpId = null;
    } else {
      this._apolloService.getVariantsByBlueprintId(blueprint.id).subscribe((v) => {
        this.expandedVariants = v;
        this.expandedBpId = blueprint.id;
      });
    }
  }

  public updateFilteredBlueprints($event): void {
    this.filteredBlueprintItems.data = [];
    this._apolloService.statusFilter = this.statusFilter; // only used for refreshing blueprints
    if (!($event.value === draftStatus.__typename || $event.value === publishedStatus.__typename)
      && (this.currentStatus === draftStatus.__typename || this.currentStatus === publishedStatus.__typename)
    ) {
      this.getArchivedBlueprints();
    } else if (($event.value === draftStatus.__typename || $event.value === publishedStatus.__typename)
      && (this.currentStatus === archivedStatus(0).__typename)
    ) {
      this._apolloService.getCurrentBlueprintsRefresh();
    } else {
      this.filteredBlueprintItems.data = this._filterBlueprintItems(this.currentBlueprintItems, this.statusFilter);
    }
    this.currentStatus = $event.value; // only used to define the 'previous' status.
  }

  private static setAssetMap(asset: PortalModuleBlueprint | PortalModuleVariant, type?: string) {
    if (asset instanceof PortalModuleVariant && type === 'featureImage') {
      return new Map().set('featureImage', asset.id.toString()).set('variant', asset.id.toString());
    } else if (asset instanceof PortalModuleBlueprint) {
      return new Map().set('blueprint', asset.id.toString());
    } else if (asset instanceof PortalModuleVariant) {
      return new Map().set('variant', asset.id.toString()).set('blueprint', asset.blueprintId.toString());
    }
  }

  private static _blueprintToInputObject(blueprint: PortalModuleBlueprint): object {
    return {
      blueprint: blueprint.toGqlInput(),
    };
  }

  public openNameDialog(event: any): void {
    const dialogRef = this._dialog.open(NewBlueprintComponent);
    dialogRef.componentInstance.result
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((c) => {
        if (!c.new) {
          // In this case we are editing an existing draft
          this.openEditBlueprintJsonDialog(event, c.blueprint);
        } else if (c.new && c.blueprint) {
          // In this case there was a blueprint without a draft, so we make one from the given blueprint
          this.openCopyBlueprintDialog(event, c.blueprint);
        } else if (c.new) {
          // In this case we make a new one without a previous blueprint of that name existing
          this.openCopyBlueprintDialog(event, undefined, c.name);
        }
      });
  }

  private _openBlueprintJsonDialog(
    $event: any,
    updater: (bp: JSON) => Observable<boolean>,
    blueprint?: PortalModuleBlueprint,
    readOnly?: boolean,
    name?: string
  ): void {
    $event.stopPropagation();
    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'blueprint',
      blueprint || new PortalModuleBlueprint(undefined, name),
      'blueprints.upload.header',
      'buttons.submit',
      this._blueprintSkipFields,
      readOnly || false,
      undefined,
      'blueprints.upload.groupId',
      blueprint?.groupId
    ));
    JsonModal.useJsonDialog(
      dialogRef,
      updater.bind(this),
      this._translateService.instant(this._blueprintFailMsg),
      this._apolloService.getBlueprintsRefresh.bind(this._apolloService)
    );
  }

  private _openConfirmationModal(
    header: string,
    content: string,
    blueprint: string,
    updater: Observable<boolean>
  ): void {
    const dialogRef = this._dialog.open(ConfirmationModalComponent,
      {
        data: {
          headerText: header,
          contentText: content,
          values: { name: blueprint },
        },
      }
    );
    ConfirmationModalComponent.useConfirmDialog(
      dialogRef,
      updater,
      this._translateService.instant(this._blueprintFailMsg),
      this._apolloService.getBlueprintsRefresh.bind(this._apolloService)
    );
  }

  private _filterBlueprintItems(blueprintList: PortalModuleBlueprint[], filter: string): PortalModuleBlueprint[] {
    let filtered = blueprintList.filter(b => b.status?.__typename === filter);
    if (filter === draftStatus.__typename) {
      // Add published blueprints for blueprints that don't have a draft yet
      const filteredGroupIds = filtered.map(x => x.groupId);
      filtered = filtered.concat(
        this.currentBlueprintItems
          .sort((a, b) => a.groupId.localeCompare(b.groupId))
          .filter(b => b.status?.__typename === publishedStatus.__typename && !filteredGroupIds.includes(b.groupId))
      );
    }
    return filtered;
  }

  public makeConfigWithVariant(variant: PortalModuleVariant): void {
    this._apolloService.makeConfigWithVariant$(variant.id)
      .subscribe(x => window.alert(this._translateService.instant('variantList.addedConfig') + x));
  }

  public sortChange(sortState: Sort): void {
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }
}
